import CheckIcon from '@mui/icons-material/Check'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { I18n, Translate } from 'common'
import { breakpoints } from '../../../../../theme/breakpoints'
import styled from 'styled-components'
import CommonStepInfo from './CommonStepInfo'
import { ITrackingHistoryModal } from './TrackingHistory'
import { StoreInstances } from '../../../../../store/StoreInstancesContainer'

const Shipped = observer(
  ({ ddsDetail }: ITrackingHistoryModal): ReactElement => {
    const { shipmentNumber, shippingId, order, dispatch, delivery } = ddsDetail
    const count = dispatch?.length ?? 0
    const unit = Translate(count === 1 ? 'itemUnit' : 'itemsUnit')
    const dateTimeOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZoneName: 'short',
    } as const

    const expectedDate = delivery?.[0]?.expectedDeliveryTime
    const deliveryTimeRaw = expectedDate ? new Date(expectedDate) : undefined

    const deliveryTime = deliveryTimeRaw
      ? new Date(deliveryTimeRaw)?.toLocaleString(
          StoreInstances.userStore.getUserLanguage(),
          dateTimeOptions
        )
      : undefined

    return (
      <TrackingContainer>
        <CommonStepInfo
          orderNumber={order?.orderNumber}
          orderDate={order?.orderDate}
          shipmentNumber={shipmentNumber}
          shippingId={shippingId}
        />
        <HorizontalDivider />

        <OrderTracking>
          {deliveryTime && (
            <OrderTrackingDate>
              <I18n name="arrivingOn" textReplacement={[deliveryTime]} />
            </OrderTrackingDate>
          )}

          <OrderTrackingList>
            <OrderTrackingItem>
              <LeftSideBoxSuccess>
                <CheckMarkSuccess />
              </LeftSideBoxSuccess>
              <I18n name="ordered" />
            </OrderTrackingItem>

            <Divider>
              <LineSuccess />
            </Divider>

            <OrderTrackingItem>
              <LeftSideBoxSuccess>
                <CheckMarkSuccess />
              </LeftSideBoxSuccess>
              <I18n name="packed" />
            </OrderTrackingItem>
            <Divider>
              <LineSuccess />
            </Divider>

            <OrderTrackingItemFullDetailed>
              <LeftSideBoxPending>
                <ShipmentCarIconPending />
                <ShipmentCheckMark />
              </LeftSideBoxPending>

              <StatusContainer>
                <StatusLabel>
                  {' '}
                  <I18n
                    name="shippedWithCount"
                    textReplacement={[`${count} ${unit}`]}
                  />
                </StatusLabel>

                <ShippingDetails>
                  <DetailsLabel>
                    <I18n name="shippingId" />:
                    <SideInfo>{dispatch?.[0]?.shipmentId}</SideInfo>
                  </DetailsLabel>
                </ShippingDetails>
              </StatusContainer>
            </OrderTrackingItemFullDetailed>

            <DividerNextStatus>
              <LineNextStatus />
            </DividerNextStatus>

            <OrderTrackingItem>
              <LeftSideBox>
                <ShipmentCarIcon />
                <ShipmentCheckMark />
              </LeftSideBox>
              <StatusDisabled>
                <I18n name="deliveredStatus" />
              </StatusDisabled>
            </OrderTrackingItem>
          </OrderTrackingList>
        </OrderTracking>

        <HorizontalDivider />

        <ShipTo>
          <I18n name="shipTo" />:
        </ShipTo>
        <Address>{order?.shipToAddress}</Address>
      </TrackingContainer>
    )
  }
)

export default Shipped

const StatusLabel = styled.p`
  font-weight: 450;
  margin: 0px;
  color: black;
`

const StatusContainer = styled.div`
  position: relative;
  margin: -2px 0px 0px 0px;
`

const DetailsLabel = styled.p`
  margin: 2px 0px 0px 0px;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  color: #9b9b9b;
`

const ShippingDetails = styled.div`
  top: 20px;
`

const SideInfo = styled.span`
  margin-left: 4px;
  font-weight: 430;
  color: black;
`

const StatusDisabled = styled.div`
  color: #adadaa;
`

const TrackingContainer = styled.div``

const OrderTracking = styled.div`
  margin-top: 16px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  display: flex;
  flex-direction: column;
`

const OrderTrackingList = styled.ul`
  padding: 0px;
  list-style-type: none;
`

const OrderTrackingItem = styled.li`
  align-items: center;
  display: flex;
  gap: 12px;
  font-size: 14px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
`

const OrderTrackingItemFullDetailed = styled(OrderTrackingItem)`
  align-items: flex-start;
  width: fit-content;
`

const HorizontalDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e4e1;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    width: 100%;
    margin: 0px;
  }
`

const LeftSideBox = styled.div`
  position: relative;
  border: 1px solid #adadaa;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding: 6px;
  border-radius: 3px;

  @media screen and (min-width: ${breakpoints.laptop}px) {
    width: 48px;
    height: 48px;
  }
`

const LeftSideBoxSuccess = styled(LeftSideBox)`
  /* Green */
  border: 1px solid #7ebd34;
`

const LeftSideBoxPending = styled(LeftSideBox)`
  /* Yellow */
  border: 1px solid #f9c100;
`

const CheckMarkSuccess = styled(CheckIcon)`
  /* Green */
  color: #7ebd34;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    width: auto;
    height: auto;
  }
`

const ShipmentCarIcon = styled(LocalShippingIcon)`
  width: 18px;
  height: 18px;
  color: #adadaa;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    width: auto;
    height: auto;
  }
`

const ShipmentCarIconPending = styled(LocalShippingIcon)`
  width: 18px;
  height: 18px;
  color: #f9c100;

  @media screen and (min-width: ${breakpoints.laptop}px) {
    width: auto;
    height: auto;
  }
`

const ShipmentCheckMark = styled(CheckIcon)`
  position: absolute;
  height: 7px;
  width: 7px;
  top: 10px;
  left: 9px;
  stroke: #ffffff;
  stroke-width: 5px;
  color: #ffffff;

  @media screen and (min-width: ${breakpoints.laptop}px) {
    height: 16px;
    width: 24px;
    left: 7.5px;
    top: 12px;
    border-radius: 0px;
    stroke-width: 1.5px;
  }
`

const Divider = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 40px;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    width: 48px;
  }
`

const Line = styled.div`
  width: 0.5px;
  height: 40px;
  padding: 0px;

  background-color: #adadaa;
`

const DividerNextStatus = styled(Divider)`
  height: 88px;
  margin-top: -50px;

  @media screen and (min-width: ${breakpoints.laptop}px) {
    margin: 0px;
    margin-top: -34px;
    margin-left: 0px;
  }
`

const LineNextStatus = styled(Line)`
  height: 88px;
`

const LineSuccess = styled.div`
  width: 0.5px;
  height: 40px;
  padding: 0px;
  /* Green */
  background-color: #7ebd34;
`

const ShipTo = styled.p`
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  margin: 23px 0px 0px 0px;
`

const Address = styled.p`
  width: 70%;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: left;
`

const OrderTrackingDate = styled.p`
  margin-top: 16px;
  width: 100%;
  height: 40px;
  background: #fef6d9;
  border-radius: 2px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 12px;
`
